// =========================================================
// 10-MODULE STATIC DATA for the Gate 33 Catering & Operations
// 1-day online training delivery platform.
//
// Schema mirrors /gate33-program/ modules so the rendering
// layout can match Module 1's design exactly:
//   hook        — short italic subtitle below the title
//   objective   — single paragraph framing what the module
//                 delivers (TrainerSection block)
//   narrative   — main body text (paragraphs separated by "\n\n")
//   pullQuote   — optional editorial pull-quote
//   placeholders — light grey image markers for the trainer
//
// All copy is PLACEHOLDER. User will refine wording and add
// real screenshots / documents directly into each module page.
// =========================================================

const HERO_PLACEHOLDER =
  'https://images.unsplash.com/photo-1661954864180-e61dea14208a?w=2000&q=85&auto=format&fit=crop';

export const CATERING_MODULES = [
  {
    n: 1,
    slug: 'reading-the-flight-brief',
    title: 'Reading and interpreting the flight brief',
    hook:
      'Decode a flight brief and extract everything that matters for catering and service planning.',
    duration_min: 45,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'By the end of this module, you will be able to open any flight brief from a premium operator and immediately map it to a working catering and service plan — without needing a second pass, without asking the dispatcher, and without missing the implicit signals that drive every downstream decision.',
    narrative:
      'A flight brief looks like an operational document. It is not. It is a compressed letter from the operator to the cabin attendant — every line is an instruction, every blank space is a question to ask before the brief becomes obsolete.\n\nDispatch info, sectors, fuel, ETA/ETD, passengers, FBOs, ground handlers, special requests — none of these fields live in isolation. The dispatch line shapes your catering window. The sectors shape your service flow. The FBO line shapes your supplier list. We map every block to a concrete downstream action so you stop reading the brief like an airline crew and start reading it like an operator.\n\nThen we go between the lines. "VIP-2", "principal on board", "discretion code 3", "no service before TOC" — those are not formatting artefacts. They are the soft signals premium operators encode into their briefs. Missing them is the difference between a flight that runs and a flight that recovers.\n\nWe finish on a workshop. You receive a redacted real-world brief and extract — in real time — every operational implication. The trainer walks the room through each one. By the end you have a mental template you reuse on every flight.',
    pullQuote:
      'The brief is not the start of the flight. It is the first cabin you walk into.',
    placeholders: [
      { id: 'cat-m01-img-01', caption: 'Sample redacted flight brief (added by trainer)' },
    ],
  },
  {
    n: 2,
    slug: 'building-the-passenger-profile',
    title: 'Building the passenger profile',
    hook:
      'Translate passenger preferences, dietary requirements, allergies, and cultural or religious considerations into concrete catering choices.',
    duration_min: 40,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will build, from any combination of operator notes, preference sheets and recurring-passenger memory, a clean passenger profile that you can execute against in catering, service and recovery without coming back for clarifications mid-flight.',
    narrative:
      'A passenger profile is not a list of allergies. It is a complete decision tree built from a handful of lines, recurring history, and the operator culture of the principal you are about to serve.\n\nDietary law (kosher, halal, vegan, low-FODMAP), cultural codes (Gulf, Asian, Indian, European UHNW families), allergies and intolerances, child meals, principal habits — each layer changes the catering you order, the way you present it, and the sequence in which it is offered.\n\nWe walk through the questions you should ask before the brief is closed. We build a profile, live, from a real passenger note — and identify every catering decision that hides inside two paragraphs of text.',
    pullQuote: null,
    placeholders: [],
  },
  {
    n: 3,
    slug: 'knowing-your-aircraft',
    title: 'Knowing your aircraft',
    hook:
      'Understand how the specific aircraft shapes every decision: galley capabilities, storage, and onboard constraints.',
    duration_min: 50,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will be able to walk onto any aircraft category — light jet to ultra-long-range — and immediately know what your galley can do, what it cannot, and how that constrains the catering you have already ordered or are about to order.',
    narrative:
      'Every aircraft has its own catering reality — galley size, refrigeration, oven capability, water capacity, storage geometry, and the silent constraints nobody writes down. Mastering them is the difference between a cabin that flows and a cabin that fights you.\n\nWe map the galley families across business jets — from Phenom and Citation light cabins to Falcon and Challenger mid-cabins, all the way up to Global, Gulfstream and BBJ/ACJ. For each, we cover storage, refrigeration, heating, water, and the operational pitfalls only experience teaches.\n\nThe module closes on a live galley walk-through. We use real layouts; you will not leave with theory, you will leave with the reflexes.',
    pullQuote: null,
    placeholders: [
      { id: 'cat-m03-img-01', caption: 'Galley reference photo (added by trainer)' },
      { id: 'cat-m03-img-02', caption: 'Storage layout reference (added by trainer)' },
    ],
  },
  {
    n: 4,
    slug: 'drafting-the-menu',
    title: 'Drafting the menu',
    hook:
      'Build a coherent, client-appropriate menu that fits the flight profile, duration, time of day, and passenger expectations.',
    duration_min: 50,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will draft a menu that reads as a single, coherent service sequence — engineered around the principal, the time of day, the duration, and the operational reality of the day. Not a list of dishes — a plan.',
    narrative:
      'A menu is not a list of dishes. It is a sequence engineered around a flight. Time of day, duration, sectors, passenger fatigue, alcohol service, child needs — every element earns its place.\n\nWe cover the architecture of a menu: starters, mains, palate cleansers, desserts, snacks, kids, crew. How it reads from first sip to top of descent. Then we pair the menu to the flight profile: short-hop lunches, red-eye long-hauls, multi-sector days. The catering logic shifts entirely with the operational shape of the day.\n\nThe workshop has you draft a complete menu for a real flight scenario. We critique it together — line by line.',
    pullQuote: null,
    placeholders: [],
  },
  {
    n: 5,
    slug: 'the-logic-behind-catering-orders',
    title: 'The logic behind catering orders',
    hook:
      'Understand why orders are structured the way they are — quantities, timing, backups, and anticipating last-minute changes.',
    duration_min: 45,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will be able to look at any catering order and read it the way a seasoned cabin attendant does — seeing the quantities, the redundancy, the timing windows, and the recovery plan that the lines hide.',
    narrative:
      'Catering orders look mechanical from the outside — quantities, items, allergies. Underneath, they encode timing, redundancy, principal habits, and the recovery plan for the changes that always come.\n\nWe break down quantities and redundancy: why operators over-order by a known margin, how to calibrate redundancy without waste, and where the budget actually goes. Then timing: lead times by city, FBO, and supplier type. The hour at which an order stops being changeable — and what to do when it changes anyway.\n\nThe trainer walks through a real catering order, line by line, and explains every decision. You walk away with the why, not just the what.',
    pullQuote: null,
    placeholders: [],
  },
  {
    n: 6,
    slug: 'onboard-equipment-and-packaging',
    title: 'Onboard equipment & packaging',
    hook:
      'Identify what equipment is available on board and choose the right packaging so food travels, stores, and presents perfectly.',
    duration_min: 40,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will know — for any catering order on any aircraft — which packaging to request, which onboard equipment will host it, and how the two work together to protect taste, presentation, and timing.',
    narrative:
      'Packaging is the bridge between the kitchen and the cabin. The wrong containers turn a perfect dish into a presentation problem at 41,000 feet. The right ones make a galley feel like a kitchen.\n\nWe cover containers, wraps, and inserts — aluminium, ovenable, vacuum-pack, glass, ceramic — when each is the right call and how they interact with onboard heating. Then cold chain and hot chain integrity: keeping ice cream frozen, mains hot, salads crisp from the kitchen to the cabin.\n\nThe module is anchored by real onboard equipment references across multiple aircraft types.',
    pullQuote: null,
    placeholders: [
      { id: 'cat-m06-img-01', caption: 'Packaging samples (added by trainer)' },
    ],
  },
  {
    n: 7,
    slug: 'understanding-the-departure-location',
    title: 'Understanding the departure location',
    hook:
      'How the location you order from changes your options, your timing, and your suppliers.',
    duration_min: 35,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will read any departure location — major European FBO or remote field — and immediately know which suppliers you can call, what timing windows apply, and what FBO constraints you need to plan around.',
    narrative:
      'Two airports, two hours apart, are not the same operational reality. Suppliers, lead times, customs, FBO services — your departure location quietly defines the boundaries of everything you can deliver onboard.\n\nWe map European hubs versus secondary airports: LBG, GVA, NCE, OSL, FAB, LCY, ZRH and beyond. Who delivers where, what changes between a primary FBO and a remote field, and how to keep your service plan intact when the location is not on your usual map.\n\nThen the silent layer — customs, security, FBO constraints. What can leave the FBO, what cannot enter, and the choreography of bringing catering airside on time.',
    pullQuote: null,
    placeholders: [],
  },
  {
    n: 8,
    slug: 'placing-orders-guests-and-crew',
    title: 'Placing orders — guests and crew',
    hook:
      'Master ordering by location: a direct order from a restaurant, or through specialist providers such as DeliSky or Private Flight Catering — and know when to use which.',
    duration_min: 55,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will know — every time — whether the right channel for an order is the restaurant directly, a white-glove specialist (DeliSky, Private Flight Catering), or a broker. And you will know how to place that order so it lands on the aircraft exactly the way you expect.',
    narrative:
      'Where you place the order changes everything: the relationship, the price, the format, the liability. Direct restaurant, white-glove specialist, broker — each model has a sweet spot and a failure mode.\n\nDirect restaurant ordering: when it is the right call. The brief, the language, the follow-up, and the packaging negotiation. Then specialist providers (DeliSky, Private Flight Catering, others) — why the channel exists, what it solves, when it is the only safe option. The cost structure, the SLA, and the operational discipline they expect from you.\n\nWe close with a live demonstration on the actual platforms — trainer-led, real screens. And we cover crew catering: often overlooked, often the difference between a sharp crew and a flat crew.',
    pullQuote: null,
    placeholders: [
      { id: 'cat-m08-img-01', caption: 'DeliSky / PFC platform reference (added by trainer)' },
    ],
  },
  {
    n: 9,
    slug: 'liaising-with-fbo-and-airport-stakeholders',
    title: 'Liaising with FBO and airport stakeholders',
    hook:
      'Coordinate effectively with the FBO and the different airport actors to ensure smooth delivery and handling.',
    duration_min: 40,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will operate the ground side of a flight the way the operators expect — knowing exactly who to call, when, in what tone, and which conversations belong on the phone versus on paper.',
    narrative:
      'The FBO is your operational partner on the ground. The handler, the customs officer, the catering driver, the security agent — each one has a job and each one can save or sink your timeline.\n\nWe walk through who is who on the ground, from the moment you land to the moment you push back. Then the communication choreography: when to call, what to say, what to never put in writing, and how to build the relationship that makes the next flight smoother.',
    pullQuote: null,
    placeholders: [],
  },
  {
    n: 10,
    slug: 'receiving-and-organising-onboard',
    title: 'Receiving and organising onboard',
    hook:
      'Receive the order, check it, and re-organise everything on board for a flawless, ready-to-serve cabin.',
    duration_min: 50,
    hero_image: HERO_PLACEHOLDER,
    objective:
      'You will receive any catering, in any setup, with the systematic discipline that turns a 30-minute window into a perfectly organised cabin ready for boarding.',
    narrative:
      'The order arrives. You have a window. Check, sort, store, label, plate-prep, brief the captain — and the cabin is ready. This module is the final operational kilometre.\n\nReceiving and checking — the systematic check you run the moment the catering arrives. What to verify, what to flag, what to reject. Then storing and labelling — how to arrange the galley so the service flow is muscle memory. Labelling conventions, allergen markers, principal-specific items.\n\nWe close with the final cabin walk-through — the last 10 minutes before passengers board. The silent checklist that separates good crew from exceptional crew.',
    pullQuote:
      'A cabin is never finished. It is only ready for the next departure.',
    placeholders: [
      { id: 'cat-m10-img-01', caption: 'Galley organised pre-boarding (added by trainer)' },
    ],
  },
];

export const CATERING_OVERVIEW = {
  title: 'Operations & Catering Management',
  subtitle: '1-day online · live + practical',
  hook:
    'The complete operational journey of a private flight catering — from flight brief to ready-to-serve cabin — taught the way premium operators actually run it.',
  walkAway:
    "By the end of the day, you'll be able to manage the entire catering and operations cycle of a private flight independently — from brief to boarding — with the professional judgement that separates good crew from exceptional crew.",
  stats: [
    { value: '10', label: 'Modules' },
    { value: '1', suffix: 'day', label: 'Live online' },
    { value: '7', suffix: 'h', label: 'Total program' },
  ],
};
